import { useMemo, useState } from 'react';

type AnyFunction = (...args: never[]) => unknown;

/**
 * Hook that wraps an async method and tracks its loading state.
 *
 * When the wrapped method returns a Promise, the hook sets isLoading to true
 * until the Promise resolves or rejects. For synchronous methods, isLoading
 * remains false.
 *
 * @param method - The method to wrap (can be sync or async)
 * @returns An object with isLoading state and the wrapped method
 *
 * @example
 * const [ isLoading, wrappedMethod ] = useLoadingForMethod(async () => {
 *   await submitForm();
 * });
 *
 * <Button onClick={wrappedMethod} disabled={isLoading}>
 *   {isLoading ? 'Submitting...' : 'Submit'}
 * </Button>
 */
export const useLoadingForMethod = <T extends AnyFunction>(
  method?: T
): [boolean, T | undefined] => {
  const [isLoading, setIsLoading] = useState(false);

  const wrappedMethod = useMemo(() => {
    if (!method) return undefined;

    return ((...args: Parameters<T>) => {
      const result = method(...args);
      if (result instanceof Promise) {
        setIsLoading(true);
        result.then(
          () => setIsLoading(false),
          () => setIsLoading(false)
        );
      }
      return result;
    }) as T;
  }, [method]);

  return [isLoading, wrappedMethod];
};
